import { Schema } from "effect";
export const JSONEncoding = {
    serialize: JSON.stringify,
    deserialize: JSON.parse,
};
export class ResponseError extends Error {
    response;
    status;
    constructor(response) {
        super(response.statusText);
        this.response = response;
        this.name = "ResponseError";
        this.status = response.status;
    }
}
export default class HttpAPI {
    auth;
    constructor(auth) {
        this.auth = auth;
    }
    async request({ schema, method, url, data, options, encoding = JSONEncoding, retries = 1, }) {
        const token = await this.auth.getToken(options?.signal);
        const headers = { Authorization: `Bearer ${token}` };
        if (data !== undefined) {
            headers["Content-Type"] = "application/json";
        }
        const request = {
            method,
            headers,
            signal: options?.signal,
            body: data !== undefined ? encoding.serialize(data) : undefined,
        };
        const response = await fetch(url, request);
        // Our token has expired, delete it and try again
        if (response.status === 401) {
            await this.auth.reset();
            if (retries > 0) {
                return await this.request({
                    schema,
                    method,
                    url,
                    data,
                    options,
                    encoding,
                    retries: retries - 1,
                });
            }
        }
        if (!response.ok) {
            // Try to extract a meaningful message from the response body to surface upstream
            let errorMessage;
            try {
                const text = await response.text();
                // try JSON first
                try {
                    const json = JSON.parse(text);
                    errorMessage = json.message ?? json.error ?? text;
                }
                catch {
                    errorMessage = text;
                }
            }
            catch {
                // We could not parse the body; log minimal context for troubleshooting
                console.warn("[HttpAPI] Could not parse error response body", {
                    method,
                    url,
                    status: response.status,
                });
            }
            const error = new ResponseError(response);
            if (errorMessage && errorMessage.length > 0) {
                // attach parsed message so callers can branch on specific messages
                error.message = errorMessage;
            }
            throw error;
        }
        const text = await response.text();
        const body = encoding.deserialize(text);
        const decode = Schema.decodeUnknownSync(schema);
        return decode(body, { errors: "all", onExcessProperty: "ignore" });
    }
}
